import Rpc from "@/Rpc";
import {LocalNotification, showLocalNotification} from "@/components/notifications/LocalNotification";
import ValueAttribute from "@/server/com/silkplm/model/plm/ValueAttribute";
import DialogsMountPoint from "@/components/dialogs/DialogsMountPoint";
import FormsLoader from "@/components/forms/FormsLoader";
import Product from "@/server/com/silkplm/model/plm/Product";
import FormModel from "@/components/forms/FormModel";
import DialogManager from "@/utils/DialogManager";
import ColorVersion from "@/server/com/silkplm/model/plm/ColorVersion";
import SizeInProductInSeason from "@/server/com/silkplm/model/plm/SizeInProductInSeason";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";
import FormViewConfig from "@/server/com/silkplm/engine/forms/views/FormViewConfig";
import _ from "lodash";
import Order from "@/server/com/silkplm/model/plm/Order";

async function validateOfChosenCategoryInUpdateDynamicAttributes(selectionLocal, form, fields, streaming, streamingEngine) {
    let correctSelection = true;
    if (!selectionLocal || !selectionLocal.length) {
        showLocalNotification(new LocalNotification($tr("cantUpdate", "Can't update"),
            $tr('noRecordsSelected', 'No records selected')));
        correctSelection = false;
        return {selectionLocal, correctSelection};
    }
    let selectionLocalExtendedEntities = [];
    if (streaming) {
        if (selectionLocal.length === 1) {
            let record = await Rpc.Forms.getExtendedEntity(Product.getRemoteClass(), selectionLocal[0].id);
            selectionLocalExtendedEntities.push(record);
        } else {
            selectionLocalExtendedEntities = await Rpc.FormsStreams.listExtendedEntitiesByStreamSelection(form.formClassLong, streamingEngine.getStreamSelection()).lock();
        }
    } else {
        let selectedIds = [];
        selectionLocal.forEach(selectedRecord => {
            selectedIds.push(selectedRecord.id);
        })
        selectionLocalExtendedEntities = await Rpc.Forms.listExtendedEntitiesByListOfIds(Product.getRemoteClass(), selectedIds).lock();
    }

    if (!selectionLocalExtendedEntities[0].productCategoryAttributes) {
        let errorMessage = "";
        if (selectionLocalExtendedEntities.length === 1) {
            errorMessage = $tr('entitiesListService.selected.product1', 'Selected Product has no Dynamic Attributes');
        } else {
            errorMessage = $tr('entitiesListService.selected.product', 'Selected Products has no Dynamic Attributes');
        }
        showLocalNotification(new LocalNotification($tr("entitiesListService.cantUpdate", "Can't update "), errorMessage));
        correctSelection = false;
        return {selectionLocalExtendedEntities, correctSelection};
    }

    let categories = new Set();
    for (let productExtendedEntity of selectionLocalExtendedEntities) {
        if (!productExtendedEntity.__supplement__.editable) {
            showLocalNotification(new LocalNotification($tr("cantEdit", "Can't edit"),
                $tr('entitiesListService.readOnly', 'Some records are read only.') +
                "<br>You can't update Dynamic Attributes."));
            correctSelection = false;
            return {selectionLocalExtendedEntities, correctSelection};
        }
        categories.add(productExtendedEntity.category);
    }
    if (categories.size > 1) {
        showLocalNotification(new LocalNotification($tr("cantUpdate", "Can't update"),
            $tr('EntitiesListService.some.record.different.categories', "Some records has different categories. <br>You can't update Dynamic Attributes.")));
        correctSelection = false;
        return {selectionLocalExtendedEntities, correctSelection};
    }
    return {selectionLocalExtendedEntities, correctSelection};
}

export async function exportDynamicAttributesToExcel(selectionLocal, form, fields, streaming, streamingEngine) {
    let result = await validateOfChosenCategoryInUpdateDynamicAttributes(selectionLocal, form, fields,
        streaming, streamingEngine);
    let correctSelection = result["correctSelection"];
    selectionLocal = result["selectionLocalExtendedEntities"];
    if (!correctSelection) {
        return;
    }
    await Rpc.ExcelExports.exportDynamicAttributesToExcel(selectionLocal);
}

export async function exportAllDynamicAttributesToExcel(streamingQuery, streamSelection) {
    await Rpc.ExcelExports.exportAllDynamicAttributesToExcel(streamingQuery, streamSelection);
}

export async function updateProductDynamicAttributes(selectionLocal, form, fields, streaming, streamingEngine) {
    let result = await validateOfChosenCategoryInUpdateDynamicAttributes(selectionLocal, form, fields,
        streaming, streamingEngine);
    let correctSelection = result["correctSelection"];
    selectionLocal = [...result["selectionLocalExtendedEntities"]];
    if (!correctSelection) {
        return;
    }
    let dynamicAttributesFieldConfig = null;
    for (let field of form.fields) {
        if (field.fieldName === "productCategoryAttributes") {
            dynamicAttributesFieldConfig = field;
        }
    }
    let dynamicAttributes = [];

    for (let el of selectionLocal[0].productCategoryAttributes) {
        let val = new ValueAttribute();
        val.categoryAttributesId = el.categoryAttributesId;
        val.dictionaryId = el.dictionaryId;
        val.name = el.name;
        val.type = el.type;
        if (selectionLocal.length > 1) {
            val.value = null;
        } else {
            val.value = el.value;

        }
        dynamicAttributes.push(val);
    }
    showUpdateDynamicAttributesDialog(dynamicAttributes, dynamicAttributesFieldConfig, selectionLocal);
}

export async function downloadXlsxTemplateForImportEntities(formClass) {
    await Rpc.ExcelExports.downloadXlsxTemplateFile(formClass);
}

function showUpdateDynamicAttributesDialog(dynamicAttributes, fieldConfig, selectionLocal) {
    DialogsMountPoint.instance.$bvModal.show("bv-modal-dynamic-attributes");
    let dialog = DialogsMountPoint.instance.$refs['updateDynamicAttributesDialog'];
    let formConfig = FormsLoader.detectFormConfig(Product);
    let productFormModel = new FormModel(formConfig);
    let validatedField = productFormModel.fields.productCategoryAttributes;
    dialog.selectedItems = selectionLocal;
    dialog.dynamicAttributes = dynamicAttributes;
    dialog.fieldConfig = fieldConfig;
    dialog.productFormModel = productFormModel;
    dialog.validatedField = validatedField;
    dialog.loadDynamicAttributes();
}

export async function assignEan(selectionLocal, className) {
    if (!selectionLocal || !selectionLocal.length) {
        showLocalNotification(new LocalNotification("Can't assign EAN", "No records selected"));
        return;
    }
    let productsHasEmptyDefaultEAN = true;
    let numberOfProductsWithFilledDefaultEAN = [];
    let listOfIdFromSelectionLocal = [];
    for (let dataRow of selectionLocal) {
        if (!dataRow.__supplement__.editable) {
            showLocalNotification(new LocalNotification($tr("EntitiesListService.cantEdit", "Can't edit"),
                $tr('EntitiesListService.someRecordsReadOnly',
                    "Some records are read only. <br>You can't assign EAN.")));
            return;
        }
        if (dataRow.eAN) {
            let product = await dataRow.getProductIdEntity();
            productsHasEmptyDefaultEAN = false;
            numberOfProductsWithFilledDefaultEAN.push(product.numberOfProduct);
        }
        listOfIdFromSelectionLocal.push(dataRow.id)
    }
    if (!productsHasEmptyDefaultEAN) {
        let errorMessage = "Products with number: <br>";
        let maxShowNumberOfProducts = 100;
        for (let index = 0; index < numberOfProductsWithFilledDefaultEAN.length; index++) {
            if (index < maxShowNumberOfProducts) {
                let el = numberOfProductsWithFilledDefaultEAN[index];
                if (index % 7 === 0 && index !== 0) {
                    errorMessage += "<br>"
                }
                errorMessage += el + ", ";
            }
        }
        errorMessage = errorMessage.slice(0, -2);
        if (numberOfProductsWithFilledDefaultEAN.length > 100) {
            errorMessage += "... <br>";
        }

        errorMessage += $tr('hasAlreadyAssignEAN.', ' has already assign EAN.');
        await DialogManager.info($tr('youCantAssignEan', "You can't assign EAN."), errorMessage);
    } else {
        let result = await Rpc.plm.EANGenerator.bulkAllocateEAN(listOfIdFromSelectionLocal, className).lock();
        if (result) {
            await DialogManager.info($tr('YouCantAssignEan', "You can't assign EAN."), result);
        }
    }
}

export async function deleteAsStream(selectedEntity, formConfig) {
    let res = await Rpc.FormsStreams.deleteEntitiesFromStream(selectedEntity.dataStream.streamEngine.getStreamSelection(),
        formConfig.formClassLong);
    if (res.error) {
        let extInfor = "";
        if (res.extendedErrorsInfo.length) {
            for (let err of res.extendedErrorsInfo) {
                if (err) {
                    extInfor += "\n" + err;
                }
            }
        }
        await DialogManager.info($tr('deleteFailed', 'Delete failed'), res.error += "\n" + extInfor);
    }

    if (res.info) {
        await DialogManager.info($tr('deleteSuccess', 'Delete success'), res.info);
    }
}

export async function afterChangeSelectionLocalSetDisabledForToolboxButtons(form, toolbox, selectionLocal) {
    let formClassLong = form.formClassLong;
    if (form.printouts) {
        for (let p in form.printouts) {
            let samplePrintoutButton = toolbox.find(value => value.label === form.printouts[p]);
            if (samplePrintoutButton) {
                if (selectionLocal?.length === 1) {
                    samplePrintoutButton.disabled = false;
                    break;
                } else {
                    samplePrintoutButton.disabled = true;
                    break;
                }
            }
        }
    }
    if (formClassLong === Order.getRemoteClass()) {
        const brandingToolboxIndex = _.findIndex(
            toolbox,
            tool => tool.label === $tr("exportBrandingFolder", "Export Branding Folder")
        );
        const contractPrintoutIndex = _.findIndex(
            toolbox,
            tool => tool.label === "Contract Printout"
        );
        let items = [];
        const isArray = Array.isArray(selectionLocal);

        if (isArray) {
            items = selectionLocal;
        } else {
            if (!selectionLocal) {
                toolbox[brandingToolboxIndex].disabled = true;
                toolbox[contractPrintoutIndex].disabled = true;
                return;
            }
            if (selectionLocal.materialize) {
                await selectionLocal.materialize();
            }
            items = Array.from(selectionLocal);
        }

        if (items.length === 1) {
            if (toolbox[brandingToolboxIndex]) {
                toolbox[brandingToolboxIndex].disabled = items[0].orderForRawMaterials;
            }
            if (toolbox[contractPrintoutIndex]) {
                toolbox[contractPrintoutIndex].disabled = items[0].orderForRawMaterials;
            }
        } else {
            if (toolbox[brandingToolboxIndex]) {
                toolbox[brandingToolboxIndex].disabled = true;
            }
            if (toolbox[contractPrintoutIndex]) {
                toolbox[contractPrintoutIndex].disabled = true;
            }
        }
    }

    if (formClassLong === "com.silkplm.model.plm.ProductInSeasonColorVersionView") {
        let buttonPrintoutLabel = $tr("toolboxButton.BoardPrintout", "Board Printout");
        let toolboxButtonPrintout = toolbox.find(value => value.label === buttonPrintoutLabel);
        if (toolboxButtonPrintout) {
            toolboxButtonPrintout.disabled = selectionLocal?.length === 0;
        }
    }
    if (formClassLong === "com.silkplm.model.plm.TechnologicalDetailsView") {
        let buttonPrintoutLabel = $tr("add.order.for.raw.materials.summary", "Bill of materials (BOM) - Summary");
        let toolboxButtonMaterials = toolbox.find(value => value.label === buttonPrintoutLabel);
        if (toolboxButtonMaterials) {
            toolboxButtonMaterials.disabled = selectionLocal?.length === 0;
        }
        let billOfMaterialsButtonLabel = $tr('add.order.for.raw.materials.button.', 'Add Order for Raw Materials');
        let toolboxBilOfMaterials = toolbox.find(value => value.label === billOfMaterialsButtonLabel);
        if (toolboxBilOfMaterials) {
            toolboxBilOfMaterials.disabled = selectionLocal?.length === 0;
        }
    }

    if (formClassLong === ProductInSeason.getRemoteClass()) {
        let assignEanButton = toolbox.find(value => value.label === $tr("assignEan", "Assign EAN"));
        if (assignEanButton) {
            assignEanButton.disabled = false;
        }
    }
    if (formClassLong === ColorVersion.getRemoteClass() ||
        formClassLong === SizeInProductInSeason.getRemoteClass()
    ) {
        let assignEanButton = toolbox.find(value => value.label === $tr("assignEan", "Assign EAN"));
        if (assignEanButton) {
            if (!selectionLocal?.length) {
                assignEanButton.disabled = true;
            } else {
                if (formClassLong === SizeInProductInSeason.getRemoteClass()) {
                    for (let selected of selectionLocal) {
                        if (selected.EAN?.length) {
                            assignEanButton.disabled = true;
                            return;
                        }
                    }
                    assignEanButton.disabled = false;
                }
                if (formClassLong === ColorVersion.getRemoteClass()) {
                    for (let selected of selectionLocal) {
                        if (selected.defaultEAN?.length) {
                            assignEanButton.disabled = true;
                            return;
                        }
                    }
                    assignEanButton.disabled = false;
                }

                if (formClassLong === ProductInSeason.getRemoteClass()) {
                    for (let selected of selectionLocal) {
                        if (selected.defaultEAN?.length) {
                            assignEanButton.disabled = true;
                            return;
                        }
                    }
                    assignEanButton.disabled = false;
                }
            }
        }
    }
}

export async function prepareToSendMarkedEntity(selectionLocal, classType) {

    if (selectionLocal.materialize) {
        await selectionLocal.materialize();
        let offerIdsList = selectionLocal.map(offer => offer.id);
        switch (classType) {
            case "planningProduct":
                await Rpc.plm.services.marketPlace.services.RemoteMethodMarketService.findMarket(offerIdsList).lock();
                break;

            case "PPSamplesProduct":
                await Rpc.plm.services.marketPlace.services.PPSampleInquiryService.findMarket(offerIdsList).lock();
                break;
        }

    } else if (selectionLocal?.length) {
        let ids = [];
        for (let entity of selectionLocal) {
            ids.push(entity.id);
        }
        switch (classType) {
            case "planningProduct":
                await Rpc.plm.services.marketPlace.services.RemoteMethodMarketService.findMarket(ids).lock();
                break;

            case "PPSamplesProduct":
                await Rpc.plm.services.marketPlace.services.PPSampleInquiryService.findMarket(ids).lock();
                break;
        }

    }
}

export async function resendAttachments(selectionLocal, isPPSample) {
    if (selectionLocal.materialize) {
        await selectionLocal.materialize();
        let entityIdsList = selectionLocal.map(entity => entity.id);
        if (isPPSample) {
            await Rpc.plm.services.marketPlace.services.PPSampleInquiryService.findMarketToResendAttachments(entityIdsList).lock();
        } else {
            await Rpc.plm.services.marketPlace.services.RemoteMethodMarketService.findMarketToResendAttachments(entityIdsList).lock();
        }
    } else if (selectionLocal?.length) {
        let ids = [];
        for (let entity of selectionLocal) {
            ids.push(entity.id);
        }
        if (isPPSample) {
            await Rpc.plm.services.marketPlace.services.PPSampleInquiryService.findMarketToResendAttachments(ids).lock();
        } else {
            await Rpc.plm.services.marketPlace.services.RemoteMethodMarketService.findMarketToResendAttachments(ids).lock();
        }
    }
}
